import axiosInstance from 'utils/axios';

/**
 * Retrieves the CRM token for the current user based on access token.
 * Requires `X-API-KEY` and `SECRET-KEY` headers (provided by interceptor or explicitly here).
 * @returns {Promise<{ success: boolean; token?: string; statusCode?: number; message?: string; raw?: any }>} Structured result for consumers.
 */
export const getCrmToken = async () => {
  try {
    // Only API keys are required per swagger; omit bearer auth
    const response = await axiosInstance.get('/api/Account/GetCRMToken', { omitAuth: true });
    const payload = response?.data;

    const statusCode = payload?.statusCode ?? response?.status;
    const token = typeof payload?.data === 'string' ? payload.data : undefined;
    const success = statusCode === 200 && !!token;

    return {
      success,
      token,
      statusCode,
      message: payload?.message || (success ? 'Token generated successfully.' : 'Invalid credentials'),
      raw: payload,
    };
  } catch (error) {
    const statusCode = error?.statusCode || error?.status || error?.response?.status;
    const message = error?.message || error?.response?.data?.message || 'Invalid credentials';
    return { success: false, statusCode, message };
  }
};

/**
 * Sets the primary contact for a user.
 * @param {{ userId: string; companyId: string; contactId: string }} payload
 * @returns {Promise<{ success: boolean; data?: any; statusCode?: number; message?: string; totalRecords?: number }>}
 */
export const setUserPrimaryContact = async ({ userId, companyId, contactId }) => {
  try {
    if (!userId || !companyId || !contactId) {
      throw new Error('User ID, company ID, and contact ID are required');
    }

    const response = await axiosInstance.post('/api/User/SetUserPrimaryContact', {
      userId,
      companyId,
      contactId,
    });

    const payload = response?.data;
    const statusCode = payload?.statusCode ?? response?.status;
    const success = statusCode === 200 && payload?.status?.toLowerCase() === 'success';

    let contactData = null;
    if (typeof payload?.data === 'string') {
      try {
        const parsedData = JSON.parse(payload.data);
        contactData = Array.isArray(parsedData) ? parsedData : [parsedData];
      } catch (parseError) {
        console.error('Error parsing primary contact data:', parseError);
        contactData = null;
      }
    } else if (Array.isArray(payload?.data)) {
      contactData = payload.data;
    } else if (payload?.data) {
      contactData = [payload.data];
    }

    return {
      success,
      data: contactData,
      statusCode,
      message: payload?.message || (success ? 'Primary contact updated successfully.' : 'Failed to update primary contact'),
      totalRecords: payload?.totalRecords ?? (Array.isArray(contactData) ? contactData.length : 0),
    };
  } catch (error) {
    const statusCode = error?.statusCode || error?.status || error?.response?.status;
    const message = error?.message || error?.response?.data?.message || 'Failed to update primary contact';
    return {
      success: false,
      statusCode,
      message,
      data: null,
      totalRecords: 0,
    };
  }
};

/**
 * Retrieves user details by ID including contact information
 * @param {string} userId - The user ID to fetch details for
 * @returns {Promise<{ success: boolean; data?: any; statusCode?: number; message?: string; totalRecords?: number }>} Structured result for consumers.
 */
export const getUserById = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const response = await axiosInstance.get('/api/User/GetUserById', {
      params: {
        userId: userId
      }
    });

    const payload = response?.data;
    const statusCode = payload?.statusCode ?? response?.status;
    const success = statusCode === 200 && payload?.status?.toLowerCase() === 'success';

    // Parse the data string if it's a JSON string
    let userData = null;
    if (typeof payload?.data === 'string') {
      try {
        const parsedData = JSON.parse(payload.data);
        if (Array.isArray(parsedData)) {
          userData = parsedData[0] || null;
        } else {
          userData = parsedData;
        }
      } catch (parseError) {
        console.error('Error parsing user data:', parseError);
        userData = null;
      }
    } else if (Array.isArray(payload?.data) && payload?.data.length > 0) {
      userData = payload.data[0];
    } else if (payload?.data) {
      userData = payload.data;
    }

    return {
      success,
      data: userData,
      statusCode,
      message: payload?.message || (success ? 'User fetched successfully.' : 'Failed to fetch user'),
      totalRecords: payload?.totalRecords || 1,
    };
  } catch (error) {
    const statusCode = error?.statusCode || error?.status || error?.response?.status;
    const message = error?.message || error?.response?.data?.message || 'Failed to fetch user';
    return {
      success: false,
      statusCode,
      message,
      data: null,
      totalRecords: 0
    };
  }
};

/**
 * Retrieves company configuration by URL.
 * @param {string} url - The current URL.
 * @returns {Promise<{ success: boolean; data?: any; message?: string }>}
 */



